import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Hotel, BadgePercent, Building, Plus, Edit, Trash2, Calendar, DollarSign, 
  LogOut, Home, Bed, TrendingUp, Users 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPropertyDialog, setShowPropertyDialog] = useState(false);
  const [showRoomDialog, setShowRoomDialog] = useState(false);
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);

  const [propertyForm, setPropertyForm] = useState({
    name: '',
    property_type: 'hotel',
    city: '',
    address: '',
    description: '',
    whatsapp: '',
    default_min_discount: 10,
    last_minute_window_days: 14
  });

  const [roomForm, setRoomForm] = useState({
    name: '',
    capacity: 2,
    default_external_price: 100,
    default_traveldiscount_price: 85,
    description: ''
  });

  const [inventoryForm, setInventoryForm] = useState({
    room_type_id: '',
    date: '',
    available_rooms: 1,
    external_reference_price: 100,
    traveldiscount_price: 85
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/owner/login');
      return;
    }

    setUser(JSON.parse(userData));
    fetchData(token);
  }, []);

  const fetchData = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [propertiesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/properties`, { headers }),
        axios.get(`${API}/bookings`, { headers })
      ]);

      setProperties(propertiesRes.data);
      setBookings(bookingsRes.data);
      
      if (propertiesRes.data.length > 0) {
        setSelectedProperty(propertiesRes.data[0]);
        fetchRooms(propertiesRes.data[0].id);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate('/owner/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async (propertyId) => {
    try {
      const response = await axios.get(`${API}/properties/${propertyId}/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Fetch rooms error:', error);
    }
  };

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/properties`, propertyForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Property created successfully!');
      setShowPropertyDialog(false);
      fetchData(token);
    } catch (error) {
      console.error('Create property error:', error);
      toast.error('Failed to create property');
    }
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/properties/${selectedProperty.id}/rooms`, roomForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Room type created successfully!');
      setShowRoomDialog(false);
      fetchRooms(selectedProperty.id);
      setRoomForm({
        name: '',
        capacity: 2,
        default_external_price: 100,
        default_traveldiscount_price: 85,
        description: ''
      });
    } catch (error) {
      console.error('Create room error:', error);
      toast.error('Failed to create room');
    }
  };

  const handleCreateInventory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/inventory`, inventoryForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Inventory updated successfully!');
      setShowInventoryDialog(false);
      setInventoryForm({
        room_type_id: '',
        date: '',
        available_rooms: 1,
        external_reference_price: 100,
        traveldiscount_price: 85
      });
    } catch (error) {
      console.error('Create inventory error:', error);
      toast.error('Failed to update inventory');
    }
  };

  const calculatePricing = async (externalPrice) => {
    try {
      const response = await axios.post(`${API}/pricing/calculate?external_price=${externalPrice}&min_discount=${selectedProperty?.default_min_discount || 10}`);
      setRoomForm(prev => ({
        ...prev,
        default_traveldiscount_price: response.data.recommended_price
      }));
      setInventoryForm(prev => ({
        ...prev,
        traveldiscount_price: response.data.recommended_price
      }));
      toast.success(`Recommended price: €${response.data.recommended_price}`);
    } catch (error) {
      console.error('Calculate pricing error:', error);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalRevenue = bookings.reduce((sum, b) => sum + b.payout_to_property, 0);
  const totalBookings = bookings.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white shadow-sm px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative w-11 h-11 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md">
            <Hotel className="w-6 h-6 text-white" />
            <div className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-sm border-2 border-white">
              <BadgePercent className="w-3.5 h-3.5 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">traveldiscount.ai</h1>
            <p className="text-xs text-gray-600">Property Dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-700">Welcome, {user?.full_name}</span>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Properties</p>
                <p className="text-3xl font-bold text-gray-900">{properties.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Building className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Bookings</p>
                <p className="text-3xl font-bold text-gray-900">{totalBookings}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Revenue (Your Payout)</p>
                <p className="text-3xl font-bold text-gray-900">€{totalRevenue.toFixed(2)}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        {properties.length === 0 ? (
          <Card className="p-12 text-center">
            <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No properties yet</h3>
            <p className="text-gray-600 mb-6">Create your first property to start listing rooms</p>
            <Dialog open={showPropertyDialog} onOpenChange={setShowPropertyDialog}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600" data-testid="create-first-property-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Property
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create New Property</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateProperty} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Property Name</label>
                      <Input
                        value={propertyForm.name}
                        onChange={(e) => setPropertyForm({...propertyForm, name: e.target.value})}
                        required
                        data-testid="property-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Type</label>
                      <select
                        className="w-full h-10 px-3 rounded-md border border-gray-300"
                        value={propertyForm.property_type}
                        onChange={(e) => setPropertyForm({...propertyForm, property_type: e.target.value})}
                        data-testid="property-type-select"
                      >
                        <option value="hotel">Hotel</option>
                        <option value="bnb">BnB</option>
                        <option value="apartment">Apartment</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">City</label>
                      <Input
                        value={propertyForm.city}
                        onChange={(e) => setPropertyForm({...propertyForm, city: e.target.value})}
                        required
                        data-testid="property-city-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">WhatsApp</label>
                      <Input
                        value={propertyForm.whatsapp}
                        onChange={(e) => setPropertyForm({...propertyForm, whatsapp: e.target.value})}
                        data-testid="property-whatsapp-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Address</label>
                    <Input
                      value={propertyForm.address}
                      onChange={(e) => setPropertyForm({...propertyForm, address: e.target.value})}
                      required
                      data-testid="property-address-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Description</label>
                    <textarea
                      className="w-full min-h-20 px-3 py-2 rounded-md border border-gray-300"
                      value={propertyForm.description}
                      onChange={(e) => setPropertyForm({...propertyForm, description: e.target.value})}
                      data-testid="property-description-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600" data-testid="property-submit-btn">
                    Create Property
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </Card>
        ) : (
          <Tabs defaultValue="rooms" className="w-full">
            <div className="flex justify-between items-center mb-6">
              <TabsList>
                <TabsTrigger value="rooms" data-testid="rooms-tab">Rooms & Inventory</TabsTrigger>
                <TabsTrigger value="bookings" data-testid="bookings-tab">Bookings</TabsTrigger>
                <TabsTrigger value="settings" data-testid="settings-tab">Property Settings</TabsTrigger>
              </TabsList>
              
              {selectedProperty && (
                <select
                  className="px-4 py-2 rounded-md border border-gray-300"
                  value={selectedProperty.id}
                  onChange={(e) => {
                    const prop = properties.find(p => p.id === e.target.value);
                    setSelectedProperty(prop);
                    fetchRooms(prop.id);
                  }}
                  data-testid="property-select"
                >
                  {properties.map(prop => (
                    <option key={prop.id} value={prop.id}>{prop.name}</option>
                  ))}
                </select>
              )}
            </div>

            <TabsContent value="rooms">
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold">Room Types</h3>
                  <Dialog open={showRoomDialog} onOpenChange={setShowRoomDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-blue-600" data-testid="add-room-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Room Type
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Room Type</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateRoom} className="space-y-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Room Name</label>
                          <Input
                            value={roomForm.name}
                            onChange={(e) => setRoomForm({...roomForm, name: e.target.value})}
                            required
                            data-testid="room-name-input"
                            placeholder="e.g., Deluxe Double Room"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">Capacity (guests)</label>
                          <Input
                            type="number"
                            value={roomForm.capacity}
                            onChange={(e) => setRoomForm({...roomForm, capacity: parseInt(e.target.value)})}
                            required
                            data-testid="room-capacity-input"
                            min="1"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">External Reference Price (€/night)</label>
                          <Input
                            type="number"
                            value={roomForm.default_external_price}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              setRoomForm({...roomForm, default_external_price: val});
                            }}
                            onBlur={() => calculatePricing(roomForm.default_external_price)}
                            required
                            data-testid="room-external-price-input"
                            step="0.01"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">TravelDiscount Price (€/night)</label>
                          <Input
                            type="number"
                            value={roomForm.default_traveldiscount_price}
                            onChange={(e) => setRoomForm({...roomForm, default_traveldiscount_price: parseFloat(e.target.value)})}
                            required
                            data-testid="room-discount-price-input"
                            step="0.01"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            After 15% commission, you'll receive ~€{(roomForm.default_traveldiscount_price * 0.85).toFixed(2)}
                          </p>
                        </div>
                        <Button type="submit" className="w-full bg-blue-600" data-testid="room-submit-btn">
                          Add Room Type
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                {rooms.length === 0 ? (
                  <Card className="p-8 text-center">
                    <Bed className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No room types yet. Add your first room type to start.</p>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {rooms.map(room => (
                      <Card key={room.id} className="p-6" data-testid={`room-card-${room.id}`}>
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="font-semibold text-lg">{room.name}</h4>
                            <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                              <Users className="w-4 h-4" />
                              {room.capacity} guests
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">External Price:</span>
                            <span className="font-semibold">€{room.default_external_price}/night</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">TravelDiscount Price:</span>
                            <span className="font-semibold text-blue-600">€{room.default_traveldiscount_price}/night</span>
                          </div>
                          <div className="flex justify-between text-green-600">
                            <span>Your payout (after 15%):</span>
                            <span className="font-semibold">€{(room.default_traveldiscount_price * 0.85).toFixed(2)}</span>
                          </div>
                        </div>
                        <Dialog open={showInventoryDialog} onOpenChange={setShowInventoryDialog}>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              className="w-full mt-4"
                              onClick={() => setInventoryForm({...inventoryForm, room_type_id: room.id})}
                              data-testid={`manage-inventory-btn-${room.id}`}
                            >
                              <Calendar className="w-4 h-4 mr-2" />
                              Manage Inventory
                            </Button>
                          </DialogTrigger>
                        </Dialog>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Inventory Dialog */}
                <Dialog open={showInventoryDialog} onOpenChange={setShowInventoryDialog}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Set Room Availability & Pricing</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateInventory} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Date</label>
                        <Input
                          type="date"
                          value={inventoryForm.date}
                          onChange={(e) => setInventoryForm({...inventoryForm, date: e.target.value})}
                          required
                          data-testid="inventory-date-input"
                          min={new Date().toISOString().split('T')[0]}
                          max={new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Available Rooms</label>
                        <Input
                          type="number"
                          value={inventoryForm.available_rooms}
                          onChange={(e) => setInventoryForm({...inventoryForm, available_rooms: parseInt(e.target.value)})}
                          required
                          data-testid="inventory-available-input"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">External Reference Price (€)</label>
                        <Input
                          type="number"
                          value={inventoryForm.external_reference_price}
                          onChange={(e) => setInventoryForm({...inventoryForm, external_reference_price: parseFloat(e.target.value)})}
                          onBlur={() => calculatePricing(inventoryForm.external_reference_price)}
                          required
                          data-testid="inventory-external-price-input"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">TravelDiscount Price (€)</label>
                        <Input
                          type="number"
                          value={inventoryForm.traveldiscount_price}
                          onChange={(e) => setInventoryForm({...inventoryForm, traveldiscount_price: parseFloat(e.target.value)})}
                          required
                          data-testid="inventory-discount-price-input"
                          step="0.01"
                        />
                      </div>
                      <Button type="submit" className="w-full bg-blue-600" data-testid="inventory-submit-btn">
                        Save Inventory
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </TabsContent>

            <TabsContent value="bookings">
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4">Recent Bookings</h3>
                {bookings.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No bookings yet</p>
                ) : (
                  <div className="space-y-4" data-testid="bookings-list">
                    {bookings.map(booking => (
                      <div key={booking.id} className="border rounded-lg p-4" data-testid={`booking-${booking.id}`}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Guest</p>
                            <p className="font-semibold">{booking.guest_name}</p>
                            <p className="text-sm text-gray-500">{booking.guest_email}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Dates</p>
                            <p className="font-semibold">{booking.check_in}</p>
                            <p className="text-sm text-gray-500">to {booking.check_out}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Total Paid</p>
                            <p className="font-semibold">€{booking.gross_amount.toFixed(2)}</p>
                            <p className="text-sm text-gray-500">Commission: €{booking.commission_amount.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Your Payout</p>
                            <p className="font-semibold text-green-600">€{booking.payout_to_property.toFixed(2)}</p>
                            <p className="text-sm text-gray-500">{booking.status}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="settings">
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4">Property Settings</h3>
                {selectedProperty && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Property Name</p>
                        <p className="font-semibold">{selectedProperty.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Type</p>
                        <p className="font-semibold capitalize">{selectedProperty.property_type}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">City</p>
                        <p className="font-semibold">{selectedProperty.city}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Minimum Discount</p>
                        <p className="font-semibold">{selectedProperty.default_min_discount}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Last-Minute Window</p>
                        <p className="font-semibold">{selectedProperty.last_minute_window_days} days</p>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default PropertyDashboard;